// Copyright (c) StrangeBee 2023

import React, { ReactElement, useCallback, useContext, useMemo } from 'react';
import { useLocalMessage } from '@Helpers/locale';
import { THLayout } from '@Molecules/THLayout';
import { useGenericQuery } from '@Queries/genericQueries';
import client from '@Client';
import { useState } from 'react';
import { useEffect } from 'react';
import { TitleSection } from '@Atoms/TitleSection/TitleSection';
import { RouteType } from '@Types';
import { generateId } from '@Helpers/string';
import THTable from '@Molecules/THTable/THTable';
import { THForm } from '@Molecules/THForm/THForm';
import lodash from 'lodash';
import RouteBuilder from '@Services/RouteBuilder';
import CortexDrawer from '@Organisms/PlatformConfig/Cortex/CortexDrawer/CortexDrawer';
import { handleNotificationError, openMessage } from '@Helpers/';
import { StatusContext } from '@Context/StatusProvider';
import { THInputNumber } from '@Molecules/Inputs/THInputNumber/THInputNumber';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THNumberSelect } from '@Molecules/Inputs/THNumberSelect/THNumberSelect';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { PermissionsEnum } from 'thehive-sdk';
import { ColumnTitle } from '@Atoms/ColumnTitle/ColumnTitle';
import { ICONS } from '@Constants/iconsApp';
import { UnavailableMessage } from '@Molecules/UnavailableMessage/UnavailableMessage';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import useValidateForm from '@Hooks/useValidateForm';
import { useDrawer } from '@Hooks/useDrawer';
import Button from 'antd/lib/button';
import usePageTitle from '@Hooks/usePageTitle';

import './CortexDetails.scss';

const CortexDetails = (): ReactElement => {
    const local = useLocalMessage();
    const { quotas, licenseCapabilities } = useContext(StatusContext);

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm({
        isValidInit: true,
    });

    const [serversStarted, setServersStarted] = useState<any[]>([]);
    const [serversUpdated, setServersUpdated] = useState<any[]>([]);
    const [serversTouched, setServersTouched] = useState(false);
    const { visible, setVisible, data: dataServer, setData: setDataServer } = useDrawer<Record<string, any>>({});

    const cortexCapabilities = useMemo<boolean>(
        () => Object.keys(licenseCapabilities.cortex || {}).includes('cortex'),
        [licenseCapabilities],
    );

    const { data } = useGenericQuery({
        fetchCallback: () => client.CortexController.getConfig(),
        key: ['get-cortex-config'],
    });

    const [cortexConfig, setCortexConfig] = useState<any>();

    const addServer = useCallback(
        (newProxy: Record<string, any>) => {
            setServersUpdated((state) => [newProxy, ...state]);
            setServersTouched(true);
            onFormValuesChange();
        },
        [setServersUpdated, onFormValuesChange],
    );

    const updateServer =
        (_id: string) =>
        (state: any): void => {
            const newListServers = serversUpdated.map((e) => (e.id === _id ? { ...state } : { ...e }));
            setServersUpdated(() => [...(newListServers || [])]);
            setServersTouched(true);
        };

    const onRemoveHandler = (id: string) => () => {
        setServersUpdated((state) => state.filter((e) => e.id !== id));
        setServersTouched(true);
    };

    const getServerData = useCallback(
        (_id?: string) => {
            const tmpConfig = { id: generateId(), auth: { type: 'bearer' } };
            if (!_id) {
                return tmpConfig;
            }

            return serversUpdated.find(({ id }) => id === _id) || tmpConfig;
        },
        [serversUpdated],
    );

    const onResetForm = () => {
        setServersTouched(false);
        setFieldsTouched(false);
        const serversTreated = serversStarted.map((e) => ({ ...e, id: generateId() }));
        setServersUpdated(serversTreated);
        form.resetFields();
    };

    const onSubmitHandler = (state: any) => {
        const computeConfig = lodash.merge(cortexConfig, state);
        const data = { ...computeConfig, servers: serversUpdated };
        client.CortexController.edit(data)
            .then(() => {
                openMessage('success', local('configPlateform.message.cortex.success'));
                setCortexConfig(data);
                setServersStarted(lodash.cloneDeep(data?.servers));
                setServersTouched(false);
                setFieldsTouched(false);
            })
            .catch(handleNotificationError);
    };

    const columns = useMemo(
        () => [
            {
                title: <ColumnTitle>{local('configPlateform.column.nameServer')}</ColumnTitle>,
                className: 'cortex-servers__name custom-table__cell',
                render: (_: any, data: Record<string, any>): ReactElement => {
                    return (
                        <div className="cortex-servers__name-title">
                            <THButton
                                type="link"
                                disabled={!cortexCapabilities}
                                onClick={() => {
                                    setDataServer(data);
                                    setVisible(true);
                                }}
                            >
                                {data.name}
                            </THButton>
                        </div>
                    );
                },
            },
            {
                className: 'cortex-servers__action custom-table__cell',
                render: (_: any, data: Record<string, any>): ReactElement => (
                    <ActionsList
                        actionList={{
                            delete: {
                                label: local('general.delete'),
                                confirm: true,
                                message: local('configPlateform.action.confirmDelete'),
                                query: () => onRemoveHandler(data.id)(),
                                disabled: !cortexCapabilities,
                                icon: <ICONS.DeleteIcon />,
                            },
                        }}
                        isIcon
                        scope="single"
                    />
                ),
            },
        ],

        [local, getServerData], //eslint-disable-line
    );

    useEffect(() => {
        if (serversStarted) {
            const serversTreated = lodash.cloneDeep(serversStarted).map((e) => ({ ...e, id: generateId() }));
            setServersUpdated(serversTreated);
        }
    }, [serversStarted]); //eslint-disable-line

    useEffect(() => {
        if (data) {
            setCortexConfig(data);
            setServersStarted(lodash.cloneDeep(data?.servers));
        }
    }, [data]);

    useEffect(() => {
        form.resetFields();
    }, [cortexConfig]); //eslint-disable-line

    usePageTitle(local('page-title.admin.platform.cortex'));

    return (
        <>
            <THLayout>
                <THLayout.Content className="cortex-layout">
                    {cortexCapabilities ? (
                        <THForm
                            form={form}
                            onFinish={onSubmitHandler}
                            autoComplete="off"
                            initialValues={cortexConfig}
                            onValuesChange={() => {
                                onFormValuesChange();
                                setFieldsTouched(true);
                            }}
                        >
                            <THForm.Layout
                                footer={
                                    <FormSubmitButton
                                        warning
                                        onCancel={onResetForm}
                                        disabled={!isValid}
                                        hidden={(!fieldsTouched && !serversTouched) || !cortexCapabilities}
                                    />
                                }
                            >
                                <div className="cortex-details__basic-informations">
                                    <TitleSection>{local('configPlateform.section.principal')}</TitleSection>
                                    <THForm.Item
                                        name={['maxRetryOnError']}
                                        className="input-half"
                                        label={local('configPlateform.field.maxRetryOnError')}
                                    >
                                        <THInputNumber
                                            disabled={!cortexCapabilities}
                                            className="cortex-input__maxretry"
                                        />
                                    </THForm.Item>

                                    <THForm.Item
                                        name={['refreshDelay']}
                                        className="cortex-input__duration input-half"
                                        label={local('configPlateform.field.refreshDelay')}
                                    >
                                        <THNumberSelect
                                            options={['seconds', 'minutes', 'hours']}
                                            disabled={!cortexCapabilities}
                                        />
                                    </THForm.Item>

                                    <THForm.Item
                                        name={['statusCheckInterval']}
                                        className="cortex-input__duration input-half"
                                        label={local('configPlateform.field.statusCheckInterval')}
                                    >
                                        <THNumberSelect
                                            options={['seconds', 'minutes', 'hours']}
                                            disabled={!cortexCapabilities}
                                        />
                                    </THForm.Item>

                                    <THForm.Item
                                        name={['jobTimeout']}
                                        className="cortex-input__duration input-half"
                                        label={local('configPlateform.field.jobTimeout')}
                                    >
                                        <THNumberSelect
                                            options={['seconds', 'minutes', 'hours']}
                                            disabled={!cortexCapabilities}
                                        />
                                    </THForm.Item>
                                </div>
                                <div className="cortex-details__servers">
                                    <TitleSection>
                                        <div>{local('configPlateform.section.servers')}</div>
                                        <CortexDrawer
                                            data={getServerData()}
                                            onSubmit={addServer}
                                            mode="add"
                                            triggerElem={
                                                <Button
                                                    type="dashed"
                                                    icon={<ICONS.PlusIcon />}
                                                    disabled={!cortexCapabilities || !quotas.cortex?.valid}
                                                    shape="circle"
                                                    size="small"
                                                />
                                            }
                                        />
                                    </TitleSection>
                                    <div className="cortex-servers__wrapper">
                                        <THTable dataSource={serversUpdated} rowKey="id" columns={columns} />
                                    </div>
                                </div>
                            </THForm.Layout>
                        </THForm>
                    ) : (
                        <UnavailableMessage type="capability" />
                    )}
                </THLayout.Content>
            </THLayout>

            {dataServer && visible && (
                <CortexDrawer
                    data={getServerData(dataServer.id)}
                    onSubmit={updateServer(dataServer.id)}
                    visible={visible}
                    afterVisible={setVisible}
                />
            )}
        </>
    );
};

const path = RouteBuilder.compute('administration.cortex');
export const Route: RouteType = {
    path,
    menu: {
        label: 'mainLayout.adminMenu.cortex',
    },
    breadcrumb: { label: 'breadcrumb.global.cortex' },
    content: <CortexDetails />,
    permissionNeeded: [PermissionsEnum.MANAGE_CONFIG],
    space: ['admin'],
    exact: true,
};
